import React from "react";
import { CircularProgress } from "@nextui-org/react";
import { useTranslation } from "react-i18next";

// Accent -> tailwind classes (icon chip + progress fill).
const ACCENTS = {
  low: { text: "text-grade-low", chip: "bg-grade-low/10", fill: "bg-grade-low" },
  medium: { text: "text-grade-medium", chip: "bg-grade-medium/10", fill: "bg-grade-medium" },
  high: { text: "text-grade-high", chip: "bg-grade-high/10", fill: "bg-grade-high" },
  primary: { text: "text-primary", chip: "bg-primary/10", fill: "bg-primary" },
  neutral: { text: "text-gray-400", chip: "bg-gray-100", fill: "bg-gray-300" },
};

const KpiStatCard = ({
  icon: Icon,
  label,
  value,
  suffix = "",
  accent = "primary",
  progress = 0,
  caption = "",
  loading = false,
  noData = false,
  onClick = null,
  // Some KPIs (e.g. a duration) have no meaningful 0-100 share to plot.
  showProgress = true,
}) => {
  const { t } = useTranslation();
  // No data -> neutral grey treatment, regardless of the requested accent.
  const accentClasses = noData ? ACCENTS.neutral : ACCENTS[accent] || ACCENTS.primary;
  const clampedProgress = noData ? 0 : Math.max(0, Math.min(100, progress || 0));
  const isClickable = typeof onClick === "function";

  return (
    <div
      onClick={onClick || undefined}
      onKeyDown={onClick || undefined}
      className={`bg-white rounded-md px-[24px] py-[20px] shadow-custom-light h-full flex flex-col gap-4 transition-shadow ${
        isClickable ? "cursor-pointer hover:shadow-md" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center ${accentClasses.chip}`}
        >
          {Icon && (
            <Icon
              className={`text-lg ${accentClasses.text}`}
              size={20}
            />
          )}
        </div>
        <span className="text-sm text-gray-500">{t(label)}</span>
      </div>

      <div className="flex items-end gap-1">
        {loading ? (
          <CircularProgress
            aria-label="Loading..."
            color="primary"
            size="sm"
          />
        ) : noData ? (
          <span className="text-[32px] leading-none font-semibold text-gray-300">{t("N/A")}</span>
        ) : (
          <span className={`text-[32px] leading-none font-semibold ${accentClasses.text}`}>
            {(value || 0).toLocaleString("fr-FR")}
            {suffix && <span className="text-2xl font-medium">{suffix}</span>}
          </span>
        )}
      </div>

      <div className="mt-auto">
        {showProgress && (
          <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${accentClasses.fill} transition-all`}
              style={{ width: `${clampedProgress}%` }}
            />
          </div>
        )}
        <div className={`text-xs text-gray-400 ${showProgress ? "mt-2" : ""}`}>
          {noData ? t("No data") : caption}
        </div>
      </div>
    </div>
  );
};

export default KpiStatCard;
